import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { LuCreditCard, LuTrash2, LuX, LuCircleAlert } from 'react-icons/lu';
import { ReactComponent as VisaSvg } from '../../assets/Payment-Logos/Visa.svg';
import { ReactComponent as MastercardSvg } from '../../assets/Payment-Logos/Mastercard.svg';
import { ReactComponent as AmexSvg } from '../../assets/Payment-Logos/Amex.svg';
import { ReactComponent as DiscoverSvg } from '../../assets/Payment-Logos/Discover-card-light.svg';
import { ReactComponent as ApplePay } from '../../assets/Payment-Logos/ApplePay.svg';
import { ReactComponent as GooglePay } from '../../assets/Payment-Logos/GooglePay.svg';
import CheckoutForm from './CheckoutForm';
import { useToaster } from '../../context/ToasterContext';
// import { PaymentRequestButtonElement } from '@stripe/react-stripe-js';

const C = {
  ink: '#0B0B0B',
  sub: '#6B7280',
  hair: '#EEF0F2',
  white: '#FFFFFF',
};

export default function PaymentOptions({
  savedMethods,
  wallets,
  selectedSavedMethod,
  selectedWallet,
  onSelectSavedMethod,
  onSelectWallet,
  paymentRequest,
  walletType,
  event,
  handlePaymentSuccess,
  refreshSavedMethods,
  onAddNewCard,
  isFreeEvent,
  displayPrice = 0,
  promoCode = '',
  isMembershipPurchase = false,
  isMembershipUpdatePayment = false,
  updatePaymentReason = 'change',
  failedPaymentMethodId = '',
  defaultPaymentMethodId = '',
  lastUsedPaymentMethodId = '',
  handleMembershipSuccess,
}) {
  const { addToast } = useToaster();

  const toastError = (message, fallback = 'Something went wrong.') => {
    addToast(String(message || fallback), 'error', 5000);
  };

  const isMembershipFlow = isMembershipPurchase || isMembershipUpdatePayment;
  const isFailedPaymentRecovery =
    isMembershipUpdatePayment && updatePaymentReason === 'failed';

  const currentCardId =
    defaultPaymentMethodId || lastUsedPaymentMethodId || '';

  const pinnedCardId = isFailedPaymentRecovery
    ? failedPaymentMethodId || currentCardId
    : currentCardId;

  const [showAllSaved, setShowAllSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addingNewCard, setAddingNewCard] = useState(false);
  const [newCardAddedInSessionId, setNewCardAddedInSessionId] = useState('');

  const disabledStyle = isFreeEvent
    ? { opacity: 0.3, pointerEvents: 'none' }
    : {};

  const [walletPreloaded, setWalletPreloaded] = useState(false);

  useEffect(() => {
    if (!walletPreloaded && walletType && paymentRequest) {
      setWalletPreloaded(true);
    }
  }, [walletPreloaded, walletType, paymentRequest]);

  useEffect(() => {
    onAddNewCard(addingNewCard);
  }, [addingNewCard, onAddNewCard]);

  useEffect(() => {
    if (isFailedPaymentRecovery) {
      if (selectedSavedMethod === failedPaymentMethodId) {
        onSelectSavedMethod(null);
      }
      return;
    }

    if (isMembershipUpdatePayment && pinnedCardId && !selectedSavedMethod) {
      onSelectSavedMethod(pinnedCardId);
      onSelectWallet('');
      return;
    }

    if (!isMembershipUpdatePayment && pinnedCardId && !selectedSavedMethod) {
      onSelectSavedMethod(pinnedCardId);
      onSelectWallet('');
    }
  }, [
    isFailedPaymentRecovery,
    isMembershipUpdatePayment,
    pinnedCardId,
    failedPaymentMethodId,
    selectedSavedMethod,
    onSelectSavedMethod,
    onSelectWallet,
  ]);

  const safeSavedMethods = Array.isArray(savedMethods) ? savedMethods : [];
  const safeWallets = Array.isArray(wallets) ? wallets : [];

  const maxCardsReached = safeSavedMethods.length >= 6;

  const brandBg = (brand) => ({
    backgroundColor:
      brand === 'visa'
        ? '#FFFFFF'
        : brand === 'mastercard'
        ? '#252525'
        : brand === 'amex'
        ? '#2F6DC8'
        : brand === 'discover'
        ? '#FFFFFF'
        : '#FFFFFF',
  });

  const sortedSavedMethods = useMemo(() => {
    const list = Array.isArray(savedMethods) ? [...savedMethods] : [];

    return list.sort((a, b) => {
      const aId = a.paymentMethodId;
      const bId = b.paymentMethodId;

      const score = (id) => {
        if (
          isFailedPaymentRecovery &&
          failedPaymentMethodId &&
          id === failedPaymentMethodId
        ) {
          return 0;
        }

        if (
          !isFailedPaymentRecovery &&
          newCardAddedInSessionId &&
          id === newCardAddedInSessionId
        ) {
          return 0;
        }

        if (
          isFailedPaymentRecovery &&
          newCardAddedInSessionId &&
          id === newCardAddedInSessionId
        ) {
          return 1;
        }

        if (pinnedCardId && id === pinnedCardId) {
          return isFailedPaymentRecovery ? 2 : 1;
        }

        return 9;
      };

      return score(aId) - score(bId);
    });
  }, [
    savedMethods,
    isFailedPaymentRecovery,
    failedPaymentMethodId,
    newCardAddedInSessionId,
    pinnedCardId,
  ]);

  const visibleSavedMethods = showAllSaved
    ? sortedSavedMethods
    : sortedSavedMethods.slice(0, 2);

  const selectedIsFailedCard =
    isFailedPaymentRecovery &&
    failedPaymentMethodId &&
    selectedSavedMethod === failedPaymentMethodId;

  const selectedIsSameAsCurrent =
    isMembershipUpdatePayment &&
    !isFailedPaymentRecovery &&
    pinnedCardId &&
    selectedSavedMethod === pinnedCardId;

  const canSubmit = (() => {
    if (isFreeEvent) return true;

    if (!selectedSavedMethod) return false;

    if (selectedIsFailedCard) return false;

    if (isMembershipUpdatePayment && selectedIsSameAsCurrent) return false;

    return true;
  })();

  const afterCardSave = async (savedCard) => {
    await refreshSavedMethods();

    const newPaymentMethodId =
      savedCard?.paymentMethodId ||
      savedCard?.savedMethod?.paymentMethodId ||
      savedCard?.savedMethod?.id ||
      savedCard?.paymentMethod?.id ||
      '';

    if (newPaymentMethodId) {
      setNewCardAddedInSessionId(newPaymentMethodId);
      onSelectSavedMethod(newPaymentMethodId);
      onSelectWallet('');
    }

    setAddingNewCard(false);
    onAddNewCard(false);

    if (isMembershipFlow && newPaymentMethodId) {
      setLoading(true);

      try {
        const token = localStorage.getItem('accessToken');

        const endpoint = isMembershipUpdatePayment
          ? `${process.env.REACT_APP_API_URL}/subscriptions/update-payment`
          : `${process.env.REACT_APP_API_URL}/subscriptions/start`;

        const { data } = await axios.post(
          endpoint,
          {
            paymentMethodId: newPaymentMethodId,
            reason: updatePaymentReason,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        if (
          !data?.success &&
          !data?._id &&
          !data?.membershipStatus &&
          !data?.membership
        ) {
          toastError(
            data?.message,
            isMembershipUpdatePayment
              ? 'Could not update card.'
              : 'Could not add card.'
          );
          return;
        }

        handleMembershipSuccess?.(data);
      } catch (e) {
        console.error(e);
        toastError(
          e?.response?.data?.message,
          isMembershipUpdatePayment
            ? 'Card update failed. Try another card.'
            : 'Could not add card. Try again.'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (paymentMethodId) => {
    if (!window.confirm('Delete this payment method?')) return;

    try {
      const { data } = await axios.post('/api/payments/delete-payment-method', {
        paymentMethodId,
      });

      if (data.success) {
        await refreshSavedMethods();
      } else {
        toastError('Failed to delete payment method.');
      }
    } catch (e) {
      console.error(e);
      toastError('Error deleting payment method.');
    }
  };

  const handleMembershipSubmit = async () => {
    const sel = safeSavedMethods.find(
      (m) => m.paymentMethodId === selectedSavedMethod
    );

    if (!sel) {
      toastError('Select a card.');
      return;
    }

    if (
      isFailedPaymentRecovery &&
      failedPaymentMethodId &&
      sel.paymentMethodId === failedPaymentMethodId
    ) {
      toastError('Please select a different card or add a new one.');
      return;
    }

    if (
      isMembershipUpdatePayment &&
      !isFailedPaymentRecovery &&
      pinnedCardId &&
      sel.paymentMethodId === pinnedCardId
    ) {
      toastError('Choose a different card to update your card.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');

      const endpoint = isMembershipUpdatePayment
        ? `${process.env.REACT_APP_API_URL}/subscriptions/update-payment`
        : `${process.env.REACT_APP_API_URL}/subscriptions/start`;

      const { data } = await axios.post(
        endpoint,
        {
          paymentMethodId: sel.paymentMethodId,
          reason: updatePaymentReason,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (!data?.success && !data?._id && !data?.membershipStatus) {
        toastError(
          data?.message,
          isMembershipUpdatePayment
            ? 'Could not update card.'
            : 'Could not add card.'
        );
        return;
      }

      handleMembershipSuccess?.(data);
    } catch (e) {
      console.error(e);
      toastError(
        e?.response?.data?.message,
        isMembershipUpdatePayment
          ? 'Card update failed. Try another card.'
          : 'Could not add card. Try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const getButtonLabel = () => {
    if (loading) return null;

    if (isMembershipUpdatePayment) return 'Update Card';

    if (isMembershipPurchase) return 'Add Card';

    return 'Pay Now';
  };

  const footerTotalLabel = 'Total';

  const footerTotalValue = `$${Number(displayPrice || 0).toFixed(2)}`;

  const footerSubText = 'Including all fees';

  const membershipFooterTitle = isMembershipUpdatePayment
    ? isFailedPaymentRecovery
      ? 'Update your card'
      : 'Change your card'
    : 'Add a reservation card';

  const membershipFooterSubText = isMembershipUpdatePayment
    ? isFailedPaymentRecovery
      ? 'Choose a different card to restore reservation access.'
      : selectedIsSameAsCurrent
      ? 'Choose a different card to continue.'
      : 'This will become your reservation card.'
    : 'Add a card to restore reservation access.';

  return (
    <div style={{ ...S.container, paddingBottom: addingNewCard ? 72 : 120 }}>
      {/*
      {walletPreloaded && !isFreeEvent && (
        <div style={{ position: 'absolute', left: -9999, top: -9999 }}>
          <PaymentRequestButtonElement options={{ paymentRequest }} />
        </div>
      )}
      */}

      <div style={{ ...S.section, ...disabledStyle }}>
        <div style={S.sectionHeader}>
          <div style={S.sectionLabel}>New Payment Method</div>
          <span style={S.maxNote}>6 cards max.</span>
        </div>

        <div
          style={{
            ...S.method,
            cursor: maxCardsReached ? 'not-allowed' : 'pointer',
            opacity: maxCardsReached ? 0.3 : 1,
          }}
          onClick={
            !maxCardsReached
              ? () => setAddingNewCard(!addingNewCard)
              : undefined
          }
        >
          <div style={S.methodLeft}>
            <LuCreditCard size={18} />
            <span style={S.newCardLabel}>Add New Card</span>
          </div>

          <div
            style={{
              ...S.arrowRight,
              transform: addingNewCard ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            ›
          </div>
        </div>

        <div
          style={{
            maxHeight: addingNewCard ? 900 : 0,
            opacity: addingNewCard ? 1 : 0,
            overflow: 'hidden',
            transition:
              'max-height 1.6s cubic-bezier(.25,.8,.25,1), opacity .5s ease',
          }}
        >
          <div style={S.addCardHeader}>
            <div style={S.brandRow}>
              <div style={{ ...S.brandBox, ...brandBg('visa') }}>
                <VisaSvg style={S.brandSvg} />
              </div>

              <div style={{ ...S.brandBox, ...brandBg('mastercard') }}>
                <MastercardSvg style={S.brandSvg} />
              </div>

              <div style={{ ...S.brandBox, ...brandBg('discover') }}>
                <DiscoverSvg style={S.brandSvg} />
              </div>

              <div style={{ ...S.brandBox, ...brandBg('amex') }}>
                <AmexSvg style={S.brandSvg} />
              </div>
            </div>

            <div
              style={S.closeButton}
              onClick={() => {
                setAddingNewCard(false);
                onAddNewCard(false);
              }}
            >
              <LuX size={16} color="#555" />
            </div>
          </div>

          <CheckoutForm
            event={event}
            paymentMethod="card"
            paymentRequest={paymentRequest}
            onSuccess={afterCardSave}
          />
        </div>
      </div>

      {!addingNewCard && (
        <>
          <div style={{ ...S.section, ...disabledStyle }}>
            <div style={S.sectionHeader}>
              <div style={S.sectionLabel}>Saved</div>

              <span
                style={{
                  ...S.viewToggle,
                  opacity: sortedSavedMethods.length > 2 ? 1 : 0.3,
                  pointerEvents: sortedSavedMethods.length > 2 ? 'auto' : 'none',
                }}
                onClick={
                  sortedSavedMethods.length > 2
                    ? () => setShowAllSaved(!showAllSaved)
                    : undefined
                }
              >
                {showAllSaved ? 'Show Less' : 'Show All'}
              </span>
            </div>

            {isFailedPaymentRecovery && failedPaymentMethodId ? (
              <div style={S.failedHelp}>
                <LuCircleAlert size={14} strokeWidth={2.4} />
                <span>Select a different card or add a new one.</span>
              </div>
            ) : null}

            {sortedSavedMethods.length === 0 ? (
              <div
                style={{ ...S.method, opacity: 0.55, cursor: 'default' }}
                aria-hidden="true"
              >
                <div style={S.methodLeft}>
                  <div
                    style={{
                      ...S.iconContainer,
                      borderStyle: 'dashed',
                      background: '#F9FAFB',
                    }}
                  >
                    <LuCreditCard size={18} style={{ color: '#9CA3AF' }} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={S.ghostTitle}>No saved methods yet</span>
                    <span style={S.ghostSub}>Your cards will appear here</span>
                  </div>
                </div>
              </div>
            ) : (
              visibleSavedMethods.map((card) => {
                const isFailedCard =
                  isFailedPaymentRecovery &&
                  failedPaymentMethodId &&
                  card.paymentMethodId === failedPaymentMethodId;

                const isCurrentCard =
                  pinnedCardId && card.paymentMethodId === pinnedCardId;

                const isNewCardAdded =
                  newCardAddedInSessionId &&
                  card.paymentMethodId === newCardAddedInSessionId;

                const isSelected = selectedSavedMethod === card.paymentMethodId;

                return (
                  <div
                    key={card.paymentMethodId}
                    style={{
                      ...S.method,
                      ...(isCurrentCard && !isFailedCard
                        ? S.currentMethod
                        : null),
                      ...(isNewCardAdded ? S.newlyAddedMethod : null),
                      ...(isFailedCard ? S.failedMethod : null),
                    }}
                    aria-disabled={isFailedCard}
                  >
                    <div style={S.methodLeft}>
                      <div
                        style={{
                          ...S.iconContainer,
                          ...brandBg(card.cardBrand),
                          ...(isFailedCard ? S.failedIconContainer : null),
                        }}
                      >
                        {card.cardBrand === 'visa' && (
                          <VisaSvg style={S.cardSvg} />
                        )}

                        {card.cardBrand === 'mastercard' && (
                          <MastercardSvg style={S.cardSvg} />
                        )}

                        {card.cardBrand === 'amex' && (
                          <AmexSvg style={S.cardSvg} />
                        )}

                        {card.cardBrand === 'discover' && (
                          <DiscoverSvg style={S.cardSvg} />
                        )}

                        {!['visa', 'mastercard', 'amex', 'discover'].includes(
                          card.cardBrand
                        ) && <LuCreditCard size={18} />}
                      </div>

                      <div style={S.cardTextStack}>
                        <span
                          style={{
                            ...S.cardNumber,
                            color: isFailedCard ? '#B91C1C' : C.ink,
                          }}
                        >
                          •••• {card.last4}
                        </span>

                        {isFailedCard ? (
                          <span style={S.failedCardNote}>Failed card</span>
                        ) : isNewCardAdded ? (
                          <span style={S.newCardNote}>New card</span>
                        ) : isCurrentCard ? (
                          <span style={S.currentCardNote}>Current card</span>
                        ) : null}
                      </div>
                    </div>

                    <div style={S.methodRight}>
                      <LuTrash2
                        size={14}
                        style={{
                          ...S.editIcon,
                          opacity: isFailedCard || isCurrentCard ? 0.35 : 1,
                          cursor:
                            isFailedCard || isCurrentCard
                              ? 'not-allowed'
                              : 'pointer',
                        }}
                        onClick={() => {
                          if (isFailedCard || isCurrentCard) return;
                          handleDelete(card.paymentMethodId);
                        }}
                      />

                      <input
                        type="radio"
                        style={S.radio}
                        checked={isSelected && !isFailedCard}
                        disabled={isFailedCard}
                        onChange={() => {
                          if (isFailedCard) return;

                          onSelectSavedMethod(card.paymentMethodId);
                          onSelectWallet('');
                        }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{ ...S.section, ...disabledStyle }}>
            <div style={S.sectionLabel}>Wallets</div>

            {safeWallets.map((w) => {
              return (
                <div
                  key={w.name}
                  aria-disabled="true"
                  title={`${w.label} is currently unavailable`}
                  style={{
                    ...S.method,
                    opacity: 0.45,
                    pointerEvents: 'none',
                    cursor: 'not-allowed',
                    filter: 'grayscale(12%)',
                  }}
                >
                  <div style={S.methodLeft}>
                    <div
                      style={{
                        ...S.iconContainer,
                        backgroundColor: w.name === 'applePay' ? '#000' : '#fff',
                      }}
                    >
                      {w.name === 'applePay' && (
                        <ApplePay style={S.walletLogo} />
                      )}

                      {w.name === 'googlePay' && (
                        <GooglePay style={S.walletLogo} />
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={S.walletLabel}>{w.label}</span>

                      <span
                        style={{
                          fontSize: 11,
                          color: '#6B7280',
                          marginTop: 2,
                        }}
                      >
                        Unavailable
                      </span>
                    </div>
                  </div>

                  <input
                    type="radio"
                    checked={false}
                    style={S.radio}
                    readOnly
                    tabIndex={-1}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}

      {!addingNewCard && (
        <div style={S.footer}>
          {isMembershipFlow ? (
            <div style={S.membershipFooterCopy}>
              <div style={S.membershipFooterTitle}>
                {membershipFooterTitle}
              </div>

              <div style={S.membershipFooterSub}>
                {membershipFooterSubText}
              </div>
            </div>
          ) : (
            <>
              <div style={S.totalPriceRow}>
                <span style={S.totalPriceLabel}>{footerTotalLabel}</span>
                <span style={S.totalPriceValue}>{footerTotalValue}</span>
              </div>

              <div style={S.totalSub}>{footerSubText}</div>
            </>
          )}

          <button
            disabled={!canSubmit || loading}
            style={{
              ...S.payButton,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
            onClick={() => {
              if (isFreeEvent) {
                setLoading(true);

                const token = localStorage.getItem('accessToken');
                const rawEventId = event?._id || event?.id;

                const cleanEventId = String(rawEventId || '').replace(
                  /^mixer-/,
                  ''
                );

                axios
                  .post(
                    `${process.env.REACT_APP_API_URL}/tickets/${cleanEventId}`,
                    {
                      amount: 0,
                      method: 'reservation_hold',
                    },
                    {
                      headers: token
                        ? { Authorization: `Bearer ${token}` }
                        : {},
                      withCredentials: true,
                    }
                  )
                  .then(({ data }) => {
                    if (!data?.ok && !data?.userTicket) {
                      toastError(
                        data?.message,
                        'Could not reserve this gathering.'
                      );
                      return;
                    }

                    handlePaymentSuccess({
                      prebuiltTicket: data.userTicket,
                      paymentMethodOverride: 'reservation_hold',
                    });
                  })
                  .catch((err) => {
                    console.error(err);

                    toastError(
                      err?.response?.data?.message,
                      'Could not reserve this gathering. Add a card or check your reservation limit.'
                    );
                  })
                  .finally(() => setLoading(false));

                return;
              }

              if (isMembershipFlow) {
                handleMembershipSubmit();
                return;
              }

              const sel = safeSavedMethods.find(
                (m) => m.paymentMethodId === selectedSavedMethod
              );

              if (!sel) {
                toastError('Select a card.');
                return;
              }

              setLoading(true);

              axios
                .post('/api/payments/charge-saved-method', {
                  paymentMethodId: sel.paymentMethodId,
                  eventId: event._id,
                  promoCode,
                })
                .then(({ data }) => {
                  if (!data?.success) {
                    toastError('Payment failed.');
                    return;
                  }

                  handlePaymentSuccess({
                    prebuiltTicket: data.userTicket,
                    paymentMethodOverride: 'card',
                  });
                })
                .catch((e) => {
                  console.error(e);
                  toastError('Payment error.');
                })
                .finally(() => setLoading(false));
            }}
          >
            {loading ? <div style={S.spinner} /> : getButtonLabel()}
          </button>
        </div>
      )}
    </div>
  );
}

const S = {
  cardSvg: {
    width: 40,
    height: 28,
    objectFit: 'contain',
  },

  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    padding: '0 20px',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  sectionLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: C.sub,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },

  viewToggle: {
    fontSize: 11,
    fontWeight: 800,
    color: '#555',
    cursor: 'pointer',
    userSelect: 'none',
  },

  failedHelp: {
    borderRadius: 10,
    background: '#FEF2F2',
    color: '#B91C1C',
    padding: '9px 10px',
    fontSize: 12,
    fontWeight: 750,
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },

  method: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 10,
    border: `1px solid ${C.hair}`,
    borderRadius: 10,
    background: '#fff',
    position: 'relative',
    overflow: 'hidden',
  },

  currentMethod: {
    border: '1px solid #D8DEE8',
    background: '#FAFAFA',
  },

  newlyAddedMethod: {
    border: '1px solid #BBF7D0',
    background: '#F0FDF4',
  },

  failedMethod: {
    border: '1px solid #FCA5A5',
    background: '#FEF2F2',
    cursor: 'not-allowed',
  },

  methodLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  methodRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    zIndex: 1,
    background: 'transparent',
  },

  iconContainer: {
    width: 40,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: `1px solid ${C.hair}`,
    overflow: 'hidden',
  },

  failedIconContainer: {
    border: '1px solid #FCA5A5',
  },

  walletLogo: {
    height: 24,
  },

  cardTextStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },

  cardNumber: {
    fontSize: 12,
    fontWeight: 650,
    color: C.ink,
  },

  currentCardNote: {
    fontSize: 10.5,
    fontWeight: 800,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },

  newCardNote: {
    fontSize: 10.5,
    fontWeight: 800,
    color: '#15803D',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },

  failedCardNote: {
    fontSize: 10.5,
    fontWeight: 800,
    color: '#B91C1C',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },

  walletLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: C.ink,
  },

  newCardLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: C.ink,
  },

  arrowRight: {
    fontSize: 20,
    color: '#888',
    fontWeight: 600,
    transition: 'transform .3s',
  },

  editIcon: {
    cursor: 'pointer',
    color: '#888',
  },

  radio: {
    width: 18,
    height: 18,
    accentColor: C.ink,
  },

  closeButton: {
    cursor: 'pointer',
    padding: 6,
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  addCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 16,
  },

  brandRow: {
    display: 'flex',
    gap: 8,
  },

  brandBox: {
    width: 45,
    height: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${C.hair}`,
    borderRadius: 6,
    background: '#fff',
    overflow: 'hidden',
  },

  brandSvg: {
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
  },

  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    padding: '14px 20px 20px',
    borderTop: `1px solid ${C.hair}`,
    zIndex: 999,
  },

  membershipFooterCopy: {
    marginBottom: 12,
  },

  membershipFooterTitle: {
    fontSize: 13,
    lineHeight: 1.15,
    fontWeight: 900,
    color: C.ink,
    letterSpacing: '-0.02em',
  },

  membershipFooterSub: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 1.3,
    fontWeight: 600,
    color: C.sub,
  },

  totalPriceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },

  totalPriceLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: C.sub,
    textTransform: 'uppercase',
  },

  totalPriceValue: {
    fontSize: 15,
    fontWeight: 900,
    color: C.ink,
    textAlign: 'right',
  },

  totalSub: {
    fontSize: 12,
    fontWeight: 600,
    color: C.sub,
    marginBottom: 12,
  },

  payButton: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: C.ink,
    color: '#fff',
    border: '1px solid ' + C.ink,
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity .2s',
  },

  maxNote: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(0,0,0,0.25)',
  },

  spinner: {
    width: 16,
    height: 16,
    border: '2px solid #fff',
    borderTop: '2px solid rgba(255,255,255,0.3)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  ghostTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: C.sub,
  },

  ghostSub: {
    fontSize: 11,
    fontWeight: 600,
    color: C.sub,
  },
};